/**
 * Per-message timestamp for the assistant transcript (SCRUM-222).
 *
 * SCOPED DELIBERATELY TO THE TIME. The ticket asks for two things — a time on
 * each message, and a date separator for older ones — and the separator ALREADY
 * EXISTS: `MessageList`'s `DayDivider`, driven by `dayKeyOf` / `dayLabelOf`.
 * Only the per-message time was missing, so that is all this module adds.
 * Re-implementing day labelling here would have put a second, subtly different
 * date mechanism next to the working one, which is how the RU/KY/EN drift in
 * SCRUM-227 happened in the first place.
 *
 * DESIGN NOTE — the design system is SILENT on message timestamps.
 * `docs/GardEn/DESIGN-SYSTEM.md` has no entry and the prototype's Chat screen
 * (`docs/GardEn/src/screens/Chat.jsx`) renders none, so per the repo rule this
 * is drafted consistent with the system rather than invented as new visual
 * language, and flagged for a design pass. The treatment is deliberately quiet
 * — the prototype's chat is uncluttered and a loud timestamp would fight it.
 *
 * Locale comes from the active i18n language via the shared `dateLocaleFor`,
 * not the browser: the user may read a Kyrgyz UI in an English-locale browser,
 * and "3:07 PM" inside an otherwise Kyrgyz thread reads as a bug. That helper
 * also carries the Chromium-has-no-`ky`-ICU-data fallback, which is exactly the
 * kind of detail a second implementation would have missed.
 */
import { dateLocaleFor } from "@/features/chat/ThreadListItem";

/**
 * `HH:MM` in the conversation's locale, or `""` when the value is unusable.
 *
 * Empty rather than throwing or printing "Invalid Date": the message itself
 * reads perfectly well without a time, so a broken timestamp costs the reader
 * more than an absent one.
 */
export function formatMessageTime(
  iso: string | undefined,
  language: string,
): string {
  if (iso === undefined || iso === "") return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  try {
    return new Date(ms).toLocaleTimeString(dateLocaleFor(language), {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    // Intl unavailable or the tag was rejected — no time is still readable.
    return "";
  }
}
